import React, { useRef } from "react";
import { Grid, Paper, Typography } from "@mui/material";
import { Pie, Bar } from "react-chartjs-2";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";

const DepartmentDistribution = ({
  departmentStats,
  people,
  onBarClick,
  onDepartmentClick,
}) => {
  const barChartRef = useRef(null);

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} md={4}>
        <Paper
          elevation={2}
          sx={{ p: 3, height: "100%", borderRadius: "12px" }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2, fontWeight: "bold", color: "#228B22" }}
          >
            Department Distribution
          </Typography>
          <div style={{ height: 250 }}>
            <Pie
              data={departmentStats.chartData}
              options={{
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "right",
                    labels: {
                      boxWidth: 12,
                      font: {
                        size: 10,
                      },
                      generateLabels: function (chart) {
                        const data = chart.data;
                        if (data.labels.length && data.datasets.length) {
                          return data.labels.map(function (label, i) {
                            const meta = chart.getDatasetMeta(0);
                            const style = meta.controller.getStyle(i);

                            return {
                              text: label,
                              fillStyle: style.backgroundColor,
                              strokeStyle: style.borderColor,
                              lineWidth: style.borderWidth,
                              hidden:
                                isNaN(data.datasets[0].data[i]) ||
                                meta.data[i].hidden,
                              index: i,
                            };
                          });
                        }
                        return [];
                      },
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        const label = context.label || "";
                        const value = context.raw || 0;
                        const total =
                          context.chart.data.datasets[0].data.reduce(
                            (a, b) => a + b,
                            0
                          );
                        const percentage = Math.round((value / total) * 100);
                        return `${label}: ${value} (${percentage}%)`;
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </Paper>
      </Grid>
      <Grid item xs={12} md={8}>
        <Paper
          elevation={2}
          sx={{ p: 3, height: "100%", borderRadius: "12px" }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2, fontWeight: "bold", color: "#228B22" }}
          >
            Staff by Department (Click bars to view details)
          </Typography>
          <div style={{ height: 250 }}>
            <Bar
              ref={barChartRef}
              data={departmentStats.barChartData}
              options={{
                maintainAspectRatio: false,
                onClick: onBarClick,
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      precision: 0,
                    },
                  },
                  x: {
                    ticks: {
                      font: {
                        size: 10,
                      },
                      maxRotation: 45,
                      minRotation: 45,
                      callback: function (value, index) {
                        return this.getLabelForValue(value);
                      },
                    },
                  },
                },
                plugins: {
                  tooltip: {
                    callbacks: {
                      title: function (tooltipItems) {
                        return tooltipItems[0].label;
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </Paper>
      </Grid>

      {/* Department Count Boxes */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} md={2}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                textAlign: "center",
                borderRadius: "12px",
                backgroundColor: "rgba(34, 139, 34, 0.9)",
                color: "white",
              }}
            >
              <PeopleIcon sx={{ fontSize: "2.5rem", mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {departmentStats.totalStaff}
              </Typography>
              <Typography variant="body2">Total Staff</Typography>
            </Paper>
          </Grid>

          {departmentStats.sortedDepts.slice(0, 9).map((dept, index) => (
            <Grid item xs={6} sm={4} md={2} key={dept}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  textAlign: "center",
                  borderRadius: "12px",
                  border: "1px solid #eee",
                  cursor: "pointer",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  },
                }}
                onClick={() => onDepartmentClick(dept)}
              >
                <BusinessIcon
                  sx={{ fontSize: "2rem", mb: 1, color: "#228B22" }}
                />
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                  {departmentStats.counts[dept]}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.85rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {dept}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default DepartmentDistribution;
